<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader"
      variant="dark"
      opacity="0.85"
      blur="2px"
      spinner-variant="primary"
      spinner-small
      rounded="sm"
    >
      <alert-handler :alert-errors="alertErrors" />
      <b-card
        aria-hidden="true"
        class="mb-0"
        title="Personal Information"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  data-vv-validate-on="blur"
                  rules="required|alpha-num"
                >
                  <b-form-group

                    :label="$t('DEBTIA_USERNAME')"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="userData.username"
                      :state="getValidationState(validationContext)"
                      trim
                      :maxlength="maxCharactersAllowed"
                      @input="characterLeft"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      :style="maxCharacters > 10 ? 'color: green' : 'color: red'"
                    >
                      {{ maxCharacters }} characters left out of {{ maxCharactersAllowed }}
                    </small>
                    <span
                      v-if="!validateUsername"
                      style="color:#ea5455"
                    >
                      Username is not valid
                    </span>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="userData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="userData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="userData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Password -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PASSWORD')"
                    label-for="password"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid':null"
                    >
                      <b-form-input
                        id="login-password"
                        v-model="userData.password"
                        :state="errors.length > 0 ? false:null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="login-password"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_LOGIN')"
                  label-for="login-status"
                >
                  <v-select
                    v-model="userData.loginStatus"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Dropdown -->
              <b-col
                v-if="!debtorsBelongToCompany"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_SELECT_CLIENT')"
                  label-for="select-client"
                >
                  <v-select
                    v-model="userData.client_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="clients"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="select-client"
                    :value="'Select Client'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Contact Person -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CONTACT_PERSON')"
                  label-for="contact-person"
                >
                  <b-form-input
                    id="contact-person"
                    v-model="userData.contact_person"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Client Type -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CLIENT_TYPE')"
                  label-for="client-type"
                >
                  <v-select
                    v-model="userData.type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="client-type"
                    :value="'private-person'"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Company Type -->
              <b-col
                v-if="userData.type === 'company'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Company Type"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COMPANY_TYPE')"
                    label-for="company-type"
                  >
                    <b-form-input
                      id="company-type"
                      v-model="userData.company_type"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: CVRCPR -->
              <b-col
                v-if="userData.type !== 'person'"
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="CVR"
                  :rules="userData.address.country_id === 247 ? 'integer|length:8' : ''"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CVRCPR')"
                    label-for="cvrcpr"
                  >
                    <b-form-input
                      id="cvrcpr"
                      v-model="userData.cvrcpr"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

              </b-col>

              <!-- Field: CPR -->
              <b-col
                v-if="userData.type === 'person'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_CPR')"
                  label-for="cpr"
                >
                  <cleave
                    id="cpr"
                    v-model="userData.cpr"
                    class="form-control"
                    :raw="false"
                    :options="options.cprDelimiter"
                  />
                </b-form-group>

              </b-col>

            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_BANK_INFORMATION') }}</h4></b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_BRANCH_NO')"
                  label-for="bankBranchNo"
                >
                  <b-form-input
                    id="bankBranchNo"
                    v-model="userData.bankBranchNo"
                    trim
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_BANK_ACCOUNT_NO')"
                  label-for="bankAccountNo"
                >
                  <b-form-input
                    id="bankAccountNo"
                    v-model="userData.bankAccountNo"
                    trim
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <hr>

            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_ADDRESS') }}</h4></b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="House Number"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_HOUSE_NUMBER')"
                    label-for="house-number"
                  >
                    <b-form-input
                      id="house-number"
                      v-model="userData.address.house_number"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: House Number -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Road Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ROAD_NAME')"
                    label-for="road-name"
                  >
                    <b-form-input
                      id="road-name"
                      v-model="userData.address.road_name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Place -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_PLACE_NAME')"
                  label-for="place"
                >
                  <b-form-input
                    id="place"
                    v-model="userData.address.place"
                    trim
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Zipcode -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Zip Code"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ZIP_CODE')"
                    label-for="zip-code"
                  >
                    <b-form-input
                      id="zip-code"
                      v-model="userData.address.zip_code"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getCityNCourtName($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: City -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="City"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_CITY')"
                    label-for="city"
                  >
                    <b-form-input
                      id="city"
                      v-model="userData.address.city"
                      :state="getValidationState(validationContext)"
                      trim
                      @change="getZipCode($event)"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Country -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  v-slot="{ errors, validated, failed }"
                  name="Country"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_COUNTRY')"
                    label-for="select-country"
                    :state="failed ? false : (validated ? true : null)"
                  >
                    <v-select
                      id="select-country"
                      v-model="userData.address.country_id"
                      :disabled="busy"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="countries"
                      :reduce="val => val.value"
                      :clearable="true"
                      placeholder="Select Country"
                      :class="{ 'is-invalid': errors.length > 0 }"
                      @input="onCountryChange"
                    >
                      <template #selected-option-container="{ option }">
                        <div class="vs__selected">
                          {{ $t(option.label) }}
                        </div>
                      </template>

                      <template #option="{ label }">
                        {{ $t(label) }}
                      </template>
                    </v-select>

                    <b-form-invalid-feedback v-if="errors[0]">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- Field: COURT -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_COURTS')"
                  label-for="court_id"
                >
                  <v-select
                    v-model="userData.address.court_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="courts"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="court_id"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <hr>
            <b-row>
              <b-col
                cols="12"
                md="12"
              ><h4>{{ $t('DEBTIA_GENERAL_INFORMATION') }}</h4></b-col>

              <!-- Fields: Custom Variables -->
              <b-col
                v-for="variable, index in debtorCustomVariables"
                :key="variable.id"
                cols="12"
                md="4"
                class="mb-1"
              >
                <div
                  v-if="variable.display_in_add_edit_page === 1 || variable.display_in_add_edit_page === true"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(variable.variable)"
                    :rules="getRules(variable.is_required, variable.type)"
                  >
                    <b-form-timepicker
                      v-if="variable.type === 'time'"
                      v-model="variable.value"
                      class="timepicker"
                    />
                    <b-input-group
                      v-else-if="variable.type === 'date'"
                    >
                      <cleave
                        id="dob"
                        v-model="debtorGeneralDates[`da${variable.variable}`]"
                        class="form-control"
                        :raw="false"
                        :options="options.date"
                        placeholder="DD-MM-YYYY"
                        @input="validateDate(0, debtorGeneralDates[`da${variable.variable}`], variable.variable, index)"
                      />
                      <b-input-group-append>
                        <b-form-datepicker
                          v-model="variable.value"
                          :date-format-options="{ day: 'numeric', month: 'numeric', year: 'numeric' }"
                          trim
                          button-only
                          right
                          size="sm"
                          @input="validateDate(1, variable.value, variable.variable, index)"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-textarea
                      v-else-if="variable.type === 'multiline string'"
                      id="textarea-rows"
                      v-model="variable.value"
                      :placeholder="$t(variable.variable)"
                      rows="3"
                    />
                    <v-select
                      v-else-if="variable.type === 'select'"
                      v-model="variable.value"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="variable.options.length > 0 ? variable.options : airportOptions"
                      :reduce="val => val.value"
                      :clearable="false"
                      input-id="debtor-login-status"
                    />
                    <b-form-radio-group
                      v-else-if="variable.type === 'boolean'"
                      v-model="variable.value"
                      :options="radioOptions"
                      value-field="item"
                      text-field="name"
                      disabled-field="notEnabled"
                      class="demo-inline-spacing"
                    />
                    <b-form-input
                      v-else-if="variable.type === 'number'"
                      v-model="variable.value"
                      type="number"
                      :placeholder="$t(variable.variable)"
                    />
                    <b-form-input
                      v-else
                      id="h-first-name"
                      v-model="variable.value"
                      :placeholder="$t(variable.variable)"
                    />
                    <small
                      :id="'text-danger-'+variable.id"
                      class="noShow"
                    >Field {{ $t(variable.variable) }} is required.</small>
                    <small class="text-danger">{{ errors[0] }}</small>
                    <label
                      :id="'dateerror-'+variable.id"
                      style="color: red; display:none;"
                    >*{{ $t('DEBTIA_DATE_FORMAT_ERROR') }}</label>
                    <!-- </b-form-group> -->
                  </validation-provider>
                </div>
              </b-col>
            </b-row>
            <hr>

            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BCard,
  BFormInvalidFeedback,
  BOverlay,
  BFormRadioGroup,
  BFormDatepicker,
  BFormTimepicker,
  BFormTextarea,
  BInputGroupAppend,
  BInputGroup,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import formValidation from '@core/comp-functions/forms/form-validation'
import { ref, reactive } from '@vue/composition-api'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import axios from '@axios'
import store from '@/store'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  required, alphaNum, email, datePattern,
} from '@validations'
import router from '@/router'
import $this from 'vue'
import Cleave from 'vue-cleave-component'
import countries from '@/countries'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cleave.js/dist/addons/cleave-phone.us'
import constants from '@/app-configs/constants'
import debtorStoreModule from '../debtorStoreModule'
import AlertHandler from './AlertHandler.vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BCard,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    AlertHandler,
    Cleave,
    BFormRadioGroup,
    BFormDatepicker,
    BFormTimepicker,
    BFormTextarea,
    BInputGroupAppend,
    BInputGroup,
  },
  mixins: [togglePasswordVisibility],
  props: {
    clients: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      busy: false,
      debtorsBelongToCompany: localStorage.getItem('debtorsBelongTo') === 'company',
      countries,
      required,
      datePattern,
      alphaNum,
      email,
      debtorCustomVariables: [],
      options: {
        cprDelimiter: {
          delimiters: ['-'],
          blocks: [6, 4],
          uppercase: true,
        },
        date: {
          date: true,
          delimiter: '-',
          datePattern: ['d', 'm', 'Y'],
        },
      },
      radioOptions: [
        { item: 'true', name: 'True' },
        { item: 'false', name: 'False' },
      ],
      airportOptions: [],
      maxCharacters: 50,
      maxCharactersAllowed: 50,
      courts: [],
      debtorGeneralDates: {},
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  setup() {
    const blankUserData = {
      username: '',
      email: '',
      name: '',
      loginStatus: '1',
      phone: null,
      password: null,
      // company: null,
      type: 'person',
      client_id: null,
      // company_type: null,
      cvrcpr: null,
      cpr: null,
      // birth_date: null,
      // billing_email: null,
      address: {
        road_name: null,
        house_number: null,
        place: null,
        zip_code: null,
        city: null,
        court_id: 0,
        country_id: 247,
      },
      bankBranchNo: null,
      bankAccountNo: null,
    }

    const userData = reactive(blankUserData)
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)

    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]

    const typeOptions = [
      { label: 'Person', value: 'person' },
      { label: 'Sole Proprietorship', value: 'sole-proprietorship' },
      { label: 'Capital Company', value: 'capital-company' },
    ]
    const alertErrors = reactive([])

    const validateUsername = ref(true)

    const loader = ref(false)

    const postCustomVariables = reactive([])

    return {
      loginOptions,
      refFormObserver,
      getValidationState,
      resetForm,
      userData,
      validateUsername,
      alertErrors,
      typeOptions,
      loader,
      postCustomVariables,
    }
  },
  created() {
    this.userData.username = this.$getRandomUsername()
    this.userData.password = this.$getRandomPassword()
    this.maxCharacters = 50 - this.$getRandomUsername().length
    this.getDebtorCustomVariables()
    this.getCourtsDropdown()
    this.createUsername()
    this.getCourtsDropdown()
  },
  methods: {
    getRules(isRequired, type) {
      let validationRules = ''
      if (isRequired === 1 && type === 'date') {
        validationRules = 'required|datePattern'
      }
      if (isRequired === 1 && type !== 'date') {
        validationRules = 'required'
      }
      if (isRequired !== 1 && type === 'date') {
        validationRules = 'datePattern'
      }
      return validationRules
    },
    validateDate(type, value, label, index) {
      if (type === 0) {
        this.debtorCustomVariables[index].value = this.$FormformattedDate(value)
      } else {
        this.debtorGeneralDates[`da${label}`] = this.$DAformattedDate(value)
      }
    },
    async onSubmit() {
      const vars = []
      // let empty = false
      this.debtorCustomVariables.forEach(element => {
        /* const elementExists = this.postCustomVariables.filter(ele => ele.customVarLabel === element.variable)
        if (elementExists.length < 1 || (elementExists.length > 0 && elementExists[0].value === '')) {
          if (element.is_required) {
            empty = true
            document.getElementById(`text-danger-${element.id}`).classList.add('alert-general-variable')
            document.getElementById(`text-danger-${element.id}`).classList.remove('noShow')
          }
        } */
        const obj = {
          id: element.id,
          value: element.value,
        }
        vars.push(obj)
      })
      this.userData.variables = vars
      const data = { ...this.userData }
      if (this.debtorsBelongToCompany) {
        data.client_id = 0
      }
      if (!data.client_id && !this.debtorsBelongToCompany) {
        this.alertErrors.push('Please Select A Client')
        return false
      }
      this.loader = true
      const validation = await axios.post('debtor/validate-username', { username: data.username })
        .then(() => true).catch(() => false)

      if (!validation) {
        this.validateUsername = validation
        this.loader = false
        return false
      }
      // this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-debtor'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, debtorStoreModule)

      store.dispatch('app-debtor/addDebtor', data)
        .then(() => {
          this.loader = true
          this.validateUsername = true
          router.replace('/debtors/list')
            .then(() => {
              $this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Debtor Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'Debtor successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors.push(error.data.message)
          this.loader = false
        })
      return true
    },
    async getDebtorCustomVariables() {
      await axios.get(`${constants.serverBaseUrl}/api/v2/debtiaapp/debtor/get-custom-variables`)
        .then(response => {
          this.debtorCustomVariables = response.data.data.variables
          this.airportOptions = response.data.data.options
          this.debtorCustomVariables.forEach(element => {
            if (element.type === 'date') {
              this.debtorGeneralDates[`da${element.variable}`] = null
            }
          })
        }).catch(() => false)
    },
    async getCourtsDropdown() {
      await axios.get('settings/courts/dropdown')
        .then(response => {
          this.courts = [{ label: 'Select Court', value: 0 }, ...response.data.data]
        }).catch(() => false)
    },
    getCityNCourtName(event) {
      const countryId = this.userData.address.country_id
      if (countryId !== 247) return
      axios.get(`settings/get-city-court-by-code?code=${event}&country_id=${countryId}`)
        .then(response => {
          if (response.data.data.city_name !== null) {
            this.userData.address.city = response.data.data.city_name
            this.userData.address.court_id = response.data.data.court_id
          }
        }).catch(error => {
          console.log(error)
        })
    },
    getZipCode(event) {
      const countryId = this.userData.address.country_id
      if (countryId !== 247) return
      axios.get(`settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          if (response.data.data.zip_code !== null) {
            this.userData.address.zip_code = response.data.data.zip_code
            this.getCityNCourtName((response.data.data.zip_code.length !== '') ? response.data.data.zip_code : 0)
          }
        }).catch(error => {
          console.log(error)
        })
    },
    async createUsername() {
      const url = `${constants.baseUrl}/create-validate/username`
      await axios.post(url, {
        type: 'client',
      }).then(response => {
        if (response.status === 200) {
          this.userData.username = response.data.data.username
        }
      })
    },
    /* customVariableChange(event, variable, id) {
      let exists = false
        this.postCustomVariables.forEach((element, index) => {
          if (element.variable === variable) {
            this.postCustomVariables[index].value = event
            exists = true
          }
        })
      }
      if (this.postCustomVariables.length === 0 || !exists) {
        this.postCustomVariables.push({
          customVarLabel: variable,
          value: event,
        })
      }
      document.getElementById(`text-danger-${id}`).classList.remove('alert-general-variable')
      document.getElementById(`text-danger-${id}`).classList.add('noShow')
    }, */
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
    onCountryChange(val) {
      if (val) {
        this.userData.address.city = null
      }
    },
  },
}
</script>

<style lang="scss">
.radiocustomvariables .custom-radio {
  margin-top: 0.75rem !important;
}

.noShow {
  display: none;
}

.alert-general-variable {
  color: red;
  display: block;
}
</style>
